import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
	"inline-flex items-center justify-center whitespace-nowrap rounded-md text-P5 font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-40",
	{
		variants: {
			variant: {
				default: "bg-primary text-primary-foreground hover:bg-primary/90",
				destructive:
					"bg-destructive text-destructive-foreground hover:bg-destructive/90",
				outline:
					"border border-input bg-background hover:bg-accent hover:text-accent-foreground",
				ghost: "hover:bg-accent hover:text-accent-foreground",

				// Custom variants
				primary:
					"bg-primary-600 font-inter text-P5 font-semibold leading-160 text-white transition-colors hover:bg-primary-700 md:text-P4",
				secondary:
					"bg-neutral-200 font-inter text-P5 font-semibold leading-160 text-white transition-colors hover:bg-neutral-300 md:text-P4",
				link: "bg-transparent font-inter text-P5 font-semibold leading-160 text-neutral-600 transition-colors hover:bg-neutral-200 hover:text-white md:text-P4",
			},
			size: {
				default: "h-10 px-4 py-2",
				sm: "h-9 rounded-md px-3",
				lg: "h-11 rounded-md px-8",
				icon: "size-10",

				// Custom variants
				primary:
					"flex h-12 items-center justify-center rounded-[8px] px-6 md:h-14",
			},
		},
		defaultVariants: {
			variant: "primary",
			size: "primary",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{ className, variant, size, asChild = false, loading, children, ...props },
		ref,
	) => {
		const Comp = asChild ? Slot : "button";
		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				disabled={loading}
				ref={ref}
				{...props}
			>
				<>
					{loading ? (
						<Loader2 className={cn("h-7 w-7 animate-spin")} />
					) : (
						children
					)}
				</>
			</Comp>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
